import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Badge } from '@chakra-ui/react';
import { FaEnvelope } from 'react-icons/fa';

import { useNavigate } from 'react-router-dom';

import { MessagesEnabled } from '@/environment';
import { routes } from '@/lib/routes';

import { ActionButton } from './ActionButton';
import { ListActionButton } from './AdList/components/ListActionButton';
import { AdListItem } from '../types';

export type AdMessagesButtonProps = {
  item: AdListItem;
  /** The grid shows its actions as icons only, the list as labelled buttons. */
  compact?: boolean;
};

/**
 * Opens the inbox filtered to one advert. Shown whenever the advert has been written about at all -
 * a conversation stays worth reaching after it has been read - with the unread count as a badge.
 */
export const AdMessagesButton: FC<AdMessagesButtonProps> = ({ item, compact }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  if (!MessagesEnabled || !item.hasMessages) {
    return null;
  }

  const label = t('myads.actions.messages');
  const badge =
    item.unreadMessageCount > 0 ? (
      <Badge colorScheme="primary" borderRadius="full">
        {item.unreadMessageCount}
      </Badge>
    ) : undefined;

  // Navigated rather than rendered as a link: the sibling actions all work this way, and an anchor
  // wrapping a button would not be valid markup.
  const handleClick = () => navigate(routes.messages({ advertId: item.id }));

  return compact ? (
    <ActionButton iconType={FaEnvelope} label={label} rightIcon={badge} onClick={handleClick} hideLabel />
  ) : (
    <ListActionButton iconType={FaEnvelope} label={label} rightIcon={badge} onClick={handleClick} />
  );
};
